import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import KopSurat from "@/components/KopSurat";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { getVisitor } from "@/lib/api";
import { formatHariTanggalID, formatDateID } from "@/lib/format";
import { useSettings } from "@/lib/useSettings";

const terbilang = (n) => {
  const kata = ["nol", "satu", "dua", "tiga", "empat", "lima", "enam", "tujuh", "delapan", "sembilan",
    "sepuluh", "sebelas"];
  if (n < 12) return kata[n];
  if (n < 20) return kata[n - 10] + " belas";
  if (n < 100) return kata[Math.floor(n / 10)] + " puluh" + (n % 10 ? " " + kata[n % 10] : "");
  if (n === 100) return "seratus";
  if (n < 200) return "seratus " + terbilang(n - 100);
  if (n < 1000) return kata[Math.floor(n / 100)] + " ratus" + (n % 100 ? " " + terbilang(n % 100) : "");
  return String(n);
};

export default function PrintSuratPernyataan() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const settings = useSettings();
  useEffect(() => { getVisitor(id).then(setData); }, [id]);
  if (!data) return <div className="p-10 text-center text-slate-500">Memuat dokumen…</div>;

  const totalPeserta = (Number(data.jumlah_tamu) || 0) + (Number(data.jumlah_pendamping) || 0);

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="no-print max-w-4xl mx-auto px-4 mb-4 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-slate-800">Pratinjau Surat Pernyataan Kunjungan</h2>
        <div className="flex gap-2">
          <Button onClick={() => window.print()} className="btn-primary" data-testid="print-trigger-surat">
            <Printer className="w-4 h-4 mr-2" /> Cetak / Simpan PDF
          </Button>
          <Button variant="outline" onClick={() => window.close()}>Tutup</Button>
        </div>
      </div>

      <div className="print-area max-w-4xl mx-auto bg-white p-10 md:p-14 shadow-xl print:shadow-none print:p-0 kop-surat">
        <KopSurat />

        <div className="mt-8 text-center">
          <div className="text-lg md:text-xl font-bold uppercase tracking-widest">Surat Pernyataan Kunjungan</div>
          <div className="text-sm underline">Nomor: {data.nomor_surat_kunjungan}</div>
        </div>

        <div className="mt-8 text-sm leading-7 text-justify">
          <p>Yang bertanda tangan di bawah ini:</p>
          <table className="my-3">
            <tbody>
              <tr><td className="w-44 align-top py-0.5">Nama</td><td className="align-top py-0.5">: {data.nama_tamu}</td></tr>
              <tr><td className="align-top py-0.5">Jabatan</td><td className="align-top py-0.5">: {data.jabatan_tamu}</td></tr>
              <tr><td className="align-top py-0.5">Instansi</td><td className="align-top py-0.5">: {data.instansi_asal}</td></tr>
              <tr><td className="align-top py-0.5">Daerah Asal</td><td className="align-top py-0.5">: {data.daerah_asal}</td></tr>
            </tbody>
          </table>

          <p>
            Dengan ini menyatakan bahwa saya beserta rombongan sejumlah{" "}
            <b>{totalPeserta} ({terbilang(totalPeserta)}) orang</b>
            {data.nama_pendamping ? ` yang terdiri dari ${data.jumlah_tamu} tamu dan ${data.jumlah_pendamping} pendamping` : ""},
            melaksanakan kunjungan resmi ke <b>{settings?.institution_name || "Sekretariat Dewan Perwakilan Rakyat Daerah"} {settings?.kabupaten_name || "Kabupaten Seruyan"}</b> pada:
          </p>

          <table className="my-3">
            <tbody>
              <tr><td className="w-44 align-top py-0.5">Hari / Tanggal</td><td className="align-top py-0.5">: {formatHariTanggalID(data.tanggal_kunjungan)}</td></tr>
              <tr><td className="align-top py-0.5">Waktu</td><td className="align-top py-0.5">: {data.waktu_kunjungan} WIB</td></tr>
              <tr><td className="align-top py-0.5">Perihal</td><td className="align-top py-0.5">: {data.perihal_kunjungan}</td></tr>
              <tr><td className="align-top py-0.5">Agenda Kunjungan</td><td className="align-top py-0.5">: {data.rencana_kunjungan}</td></tr>
              <tr><td className="align-top py-0.5">Dasar Surat Tugas</td><td className="align-top py-0.5">: {data.nomor_surat_tugas}</td></tr>
              <tr><td className="align-top py-0.5">Narahubung</td><td className="align-top py-0.5">: {data.penanggung_jawab}</td></tr>
            </tbody>
          </table>

          <p>
            Kami menyatakan bahwa seluruh keterangan di atas adalah benar dan kunjungan ini kami laksanakan
            dengan menjunjung tinggi tata tertib, protokol, dan etika yang berlaku di lingkungan
            {" "}{settings?.institution_name || "Sekretariat DPRD"} {settings?.kabupaten_name || "Kabupaten Seruyan"}. Demikian surat pernyataan ini dibuat untuk dapat
            dipergunakan sebagaimana mestinya.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-10 text-sm">
          <div>
            <div>Mengetahui,</div>
            <div>Penanggung Jawab / Narahubung,</div>
            <div className="h-24" />
            <div className="font-bold underline">{data.penanggung_jawab}</div>
          </div>
          <div className="text-right">
            <div>{settings?.kota_ttd || "Kuala Pembuang"}, {formatDateID(data.tanggal_kunjungan)}</div>
            <div>Yang membuat pernyataan,</div>
            <div className="h-24" />
            <div className="font-bold underline">{data.nama_tamu}</div>
            <div>{data.jabatan_tamu}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
